import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';

/**
 * Deferred revenue and expenses (modules/accounting deferrals.ts): an amount booked at once but
 * earned or used up over months, brought into profit and loss month by month.
 */

type Kind = 'revenue' | 'expense';
interface Row {
  id: string;
  kind: Kind;
  name: string;
  account: string;
  account_code?: string;
  account_name?: string;
  amount: number;
  start: string;
  months: number;
  last: string;
  partner: string | null;
  reference: string | null;
  state: 'active' | 'ended';
  ended_on: string | null;
  recognized: number;
  left: number;
}
interface List {
  today: string;
  rows: Row[];
  totals: Record<Kind, number>;
}
interface Detail {
  deferral: Row;
  today: string;
  months: { month: string; date: string; amount: number; entry: string | null; number: number | null; cancelled: boolean; done: boolean }[];
  end: { entry: string; number: number; date: string; amount: number } | null;
  recognized: number;
  left: number;
  first: string | null;
}
export interface PickAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  active: boolean;
}

export function DeferralsTab({ accounts, version, onEntry, onChanged }: { accounts: PickAccount[]; version: number; onEntry: (id: string) => void; onChanged: () => void }) {
  const { can } = useSession();
  const [kind, setKind] = useState<Kind>('revenue');
  const [v, setV] = useState(0);
  const { data } = useAction<List>('accounting.deferrals', { v: version + v });
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const changed = () => {
    setV((x) => x + 1);
    onChanged();
  };
  const rows = data?.rows.filter((r) => r.kind === kind) ?? [];
  return (
    <div className="rep-grid">
      <section className="panel rep-wide" aria-labelledby="def-title">
        <div className="panel-head">
          <div>
            <h2 id="def-title">{t('def.title')}</h2>
            <p className="muted">{t('def.hint')}</p>
          </div>
          {can('accounting.post') ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              {t('def.new')}
            </Button>
          ) : null}
        </div>
        <div className="def-head">
          <div className="segmented" role="radiogroup" aria-label={t('def.title')}>
            {(['revenue', 'expense'] as const).map((k) => (
              <button key={k} role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
                {t(`def.k_${k}`)}
              </button>
            ))}
          </div>
          {data ? <p className="muted">{t(kind === 'revenue' ? 'def.waiting_revenue' : 'def.waiting_expense', { amount: money(data.totals[kind]) })}</p> : null}
        </div>
        {!data ? (
          <Skeleton h={160} />
        ) : !rows.length ? (
          <EmptyState icon="calendar" title={t(kind === 'revenue' ? 'def.none_revenue' : 'def.none_expense')} body={t(kind === 'revenue' ? 'def.none_revenue_hint' : 'def.none_expense_hint')} />
        ) : (
          <Table
            caption={t(`def.k_${kind}`)}
            head={[t('def.c_name'), t('def.c_period'), t('acc.c_amount'), t('def.c_done'), t('def.c_left')]}
            phoneHide={[1, 3]}
            left={[1]}
            rows={rows.map((r) => ({
              key: r.id,
              onClick: () => setOpen(r.id),
              cells: [
                <span className="acc-memo">
                  {r.name}
                  <span className="muted small">
                    {[r.account_code ? `${r.account_code} ${r.account_name}` : null, r.partner, r.state === 'ended' ? t('def.ended_on', { date: day(r.ended_on!) }) : null].filter(Boolean).join(' · ')}
                  </span>
                </span>,
                <span>
                  {t('rep.range', { from: day(r.start), to: day(r.last) })}
                  <span className="muted small"> · {tn('def.months', r.months)}</span>
                </span>,
                <span className="tabular">{money(r.amount)}</span>,
                <span className="tabular">{money(r.recognized)}</span>,
                <span className="tabular strong">{r.left ? money(r.left) : '—'}</span>,
              ],
            }))}
          />
        )}
      </section>
      {creating ? <DeferralForm kind={kind} accounts={accounts} onClose={() => setCreating(false)} onDone={(id) => (setCreating(false), changed(), setOpen(id))} /> : null}
      {open ? <DeferralDialog id={open} onClose={() => setOpen(null)} onEntry={onEntry} onChanged={changed} /> : null}
    </div>
  );
}

function DeferralForm({ kind: initial, accounts, onClose, onDone }: { kind: Kind; accounts: PickAccount[]; onClose: () => void; onDone: (id: string) => void }) {
  const { busy, run } = useSave();
  const [kind, setKind] = useState<Kind>(initial);
  const [name, setName] = useState('');
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [start, setStart] = useState(todayIso());
  const [months, setMonths] = useState('12');
  const [partner, setPartner] = useState('');
  const [reference, setReference] = useState('');
  const [tried, setTried] = useState(false);
  const options = accounts.filter((a) => a.active && a.type === (kind === 'revenue' ? 'income' : 'expense'));
  const cents = parseMoney(amount);
  const n = /^\d{1,3}$/.test(months.trim()) ? Number(months) : NaN;
  const errors = {
    name: name.trim().length < 3 ? t('def.name_rule') : null,
    account: !account ? t('def.account_rule') : null,
    amount: !cents ? t('def.amount_rule') : null,
    months: !(n >= 2 && n <= 120) ? t('def.months_rule') : null,
    start: !/^\d{4}-\d{2}-\d{2}$/.test(start) ? t('def.start_rule') : null,
  };
  const bad = Object.values(errors).some(Boolean);
  const each = cents && n >= 2 && n <= 120 ? Math.floor(cents / n) : null;
  const save = async () => {
    setTried(true);
    if (bad) return;
    let id = '';
    const ok = await run(
      async () => {
        const d = await action<{ id: string }>('accounting.save_deferral', {
          kind,
          name: name.trim(),
          account,
          amount: cents,
          start,
          months: n,
          partner: partner.trim() || null,
          reference: reference.trim() || null,
        });
        id = d.id;
      },
      t('def.saved', { name: name.trim() }),
    );
    if (ok) onDone(id);
  };
  return (
    <Dialog
      title={t('def.new')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
            {t('def.create')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="segmented" role="radiogroup" aria-label={t('def.kind')}>
          {(['revenue', 'expense'] as const).map((k) => (
            <button key={k} role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => (setKind(k), setAccount(''))}>
              {t(`def.k_${k}`)}
            </button>
          ))}
        </div>
        <p className="muted">{t(kind === 'revenue' ? 'def.explain_revenue' : 'def.explain_expense')}</p>
        <Field label={t('def.c_name')} error={tried ? errors.name : null}>
          <input
            className="input"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(kind === 'revenue' ? 'def.name_ph_revenue' : 'def.name_ph_expense')}
            data-autofocus
          />
        </Field>
        <Field label={t(kind === 'revenue' ? 'def.account_revenue' : 'def.account_expense')} hint={t('def.account_hint')} error={tried ? errors.account : null}>
          <select className="input" value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">{t('def.choose')}</option>
            {options.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-grid">
          <Field label={t('acc.c_amount')} error={tried ? errors.amount : null}>
            <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label={t('def.start')} error={tried ? errors.start : null}>
            <input className="input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label={t('def.over_months')} error={tried ? errors.months : null}>
            <input className="input" inputMode="numeric" maxLength={3} value={months} onChange={(e) => setMonths(e.target.value)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('def.partner')}>
            <input className="input" maxLength={120} value={partner} onChange={(e) => setPartner(e.target.value)} />
          </Field>
          <Field label={t('acc.c_reference')}>
            <input className="input" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        </div>
        {each ? (
          <p className="callout callout-info" role="status">
            <Icon name="calendar" size={16} />{' '}
            {t('def.preview', { amount: money(each), n, first: day(new Date(Date.UTC(Number(start.slice(0, 4)), Number(start.slice(5, 7)), 0)).toISOString().slice(0, 10)) })}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function DeferralDialog({ id, onClose, onEntry, onChanged }: { id: string; onClose: () => void; onEntry: (id: string) => void; onChanged: () => void }) {
  const { can } = useSession();
  const [v, setV] = useState(0);
  const { data } = useAction<Detail>('accounting.deferral', { deferral: id, v });
  const [ending, setEnding] = useState(false);
  const d = data?.deferral;
  return (
    <Dialog
      title={d?.name ?? t('def.title')}
      onClose={onClose}
      size="lg"
      footer={
        d && d.state === 'active' && can('accounting.post') ? (
          <Button variant="secondary" icon="pause" onClick={() => setEnding(true)}>
            {t('def.end')}
          </Button>
        ) : null
      }
    >
      {!data || !d ? (
        <Skeleton h={240} />
      ) : (
        <div className="acc-ledger">
          <p className="muted">
            {[t(`def.k_${d.kind}`), d.account_code ? `${d.account_code} ${d.account_name}` : null, d.partner, d.reference].filter(Boolean).join(' · ')}
            {d.state === 'ended' ? (
              <>
                {' '}
                <StatusBadge tone="neutral" icon="pause" label={t('def.ended_on', { date: day(d.ended_on!) })} />
              </>
            ) : null}
          </p>
          <dl className="fiscal-stats">
            <div className="fiscal-stat">
              <dt>{t('acc.c_amount')}</dt>
              <dd>{money(d.amount)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t(d.kind === 'revenue' ? 'def.earned' : 'def.used')}</dt>
              <dd>{money(data.recognized)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('def.c_left')}</dt>
              <dd>{money(data.left)}</dd>
            </div>
          </dl>
          <Table
            caption={t('def.schedule')}
            head={[t('def.c_month'), t('acc.c_amount'), t('def.c_state')]}
            sortable={false}
            phoneHide={[]}
            rows={[
              ...data.months.map((m) => ({
                key: m.month,
                onClick: m.entry ? () => onEntry(m.entry!) : undefined,
                cells: [
                  <span>
                    {day(m.date)}
                    {m.number ? <span className="muted small"> · {t('acc.entry_title', { n: m.number })}</span> : null}
                  </span>,
                  <span className={`tabular${m.cancelled ? ' muted strike' : ''}`}>{money(m.amount)}</span>,
                  m.cancelled ? (
                    <StatusBadge tone="neutral" icon="x" label={t('def.s_cancelled')} />
                  ) : m.done ? (
                    <StatusBadge tone="ok" icon="check" label={t(d.kind === 'revenue' ? 'def.s_earned' : 'def.s_used')} />
                  ) : (
                    <StatusBadge tone="info" icon="clock" label={t('def.s_planned')} />
                  ),
                ],
              })),
              ...(data.end
                ? [
                    {
                      key: 'end',
                      onClick: () => onEntry(data.end!.entry),
                      cells: [
                        <span>
                          {day(data.end.date)} <span className="muted small"> · {t('def.rest_at_end')}</span>
                        </span>,
                        <span className="tabular">{money(data.end.amount)}</span>,
                        <StatusBadge tone="ok" icon="check" label={t(d.kind === 'revenue' ? 'def.s_earned' : 'def.s_used')} />,
                      ],
                    },
                  ]
                : []),
            ]}
          />
        </div>
      )}
      {ending && d ? <EndDialog d={d} onClose={() => setEnding(false)} onDone={() => (setEnding(false), setV((x) => x + 1), onChanged())} /> : null}
    </Dialog>
  );
}

function EndDialog({ d, onClose, onDone }: { d: Row; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const [date, setDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const ok = /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= d.start && reason.trim().length >= 3;
  const end = async () => {
    if (await run(() => action('accounting.end_deferral', { deferral: d.id, date, reason: reason.trim() }), t('def.ended', { name: d.name }))) onDone();
  };
  return (
    <Dialog
      title={t('def.end_title', { name: d.name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="pause" busy={busy} disabled={!ok} onClick={() => void end()}>
            {t('def.end')}
          </Button>
        </>
      }
    >
      <p className="muted">{t(d.kind === 'revenue' ? 'def.end_hint_revenue' : 'def.end_hint_expense')}</p>
      <Field label={t('def.end_date')}>
        <input className="input" type="date" min={d.start} value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label={t('def.reason')}>
        <input className="input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('def.reason_ph')} />
      </Field>
    </Dialog>
  );
}
